import { blanksAsLabels, blanksAsLines } from "./blanks";
import { parseBlocks, stripInline, toPlainText } from "./markdown";
import { displayRows } from "./formulas";

// The files a make can be downloaded as. The big file libraries only load when someone
// downloads that kind of file.
export const FILE_FORMATS = {
  docx: { label: "Word document", short: "Word", extension: "docx" },
  pdf: { label: "PDF", short: "PDF", extension: "pdf" },
  xlsx: { label: "Spreadsheet", short: "Excel", extension: "xlsx" },
  pptx: { label: "PowerPoint", short: "PowerPoint", extension: "pptx" },
  png: { label: "Picture", short: "PNG", extension: "png" },
  txt: { label: "Text file", short: "Text", extension: "txt" },
  csv: { label: "CSV", short: "CSV", extension: "csv" },
};

// Each output's own file, and the other formats it can also be saved as
export const OUTPUT_FILES = {
  docx: { format: "docx", others: ["pdf", "txt"] },
  pdf: { format: "pdf", others: ["docx", "txt"] },
  xlsx: { format: "xlsx", others: ["csv"] },
  pptx: { format: "pptx", others: ["docx", "pdf"] },
  image: { format: "png", others: [] },
  text: { format: "txt", others: ["docx", "pdf"] },
};

export function fileName(title, extension) {
  const name =
    (title || "Make")
      .replace(/[\\/:*?"<>|]+/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80) || "Make";
  return `${name}.${extension}`;
}

function save(url, name) {
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function csvCell(value) {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

// Every table, one after another, with worked-out values in place of formulas
function toCsv(text) {
  return parseBlocks(text)
    .filter((block) => block.type === "table")
    .map((block) =>
      displayRows([block.header, ...block.rows])
        .map((row) => row.map((cell) => csvCell(stripInline(cell))).join(","))
        .join("\r\n")
    )
    .join("\r\n\r\n");
}

// `format` is one of FILE_FORMATS; `plain` says the make is plain text, not Markdown
export async function downloadFile({
  format,
  text: made,
  title,
  image,
  plain,
}) {
  const file = FILE_FORMATS[format];
  if (!file) return;
  const name = fileName(title, file.extension);
  if (format === "png") {
    save(image, name);
    return;
  }
  // Blanks nobody filled in become lines to write on, or [labels] in plain text
  const text = blanksAsLines(made);
  let blob;
  if (format === "txt") {
    const content = plain ? blanksAsLabels(made) : toPlainText(made);
    blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  } else if (format === "csv") {
    blob = new Blob(["﻿" + toCsv(text)], {
      type: "text/csv;charset=utf-8",
    });
  } else if (format === "docx") {
    const { buildDocx } = await import(
      /* webpackChunkName: "file-docx" */ "./files/docx"
    );
    blob = await buildDocx(text);
  } else if (format === "pdf") {
    const { buildPdf } = await import(
      /* webpackChunkName: "file-pdf" */ "./files/pdf"
    );
    blob = await buildPdf(text, title);
  } else if (format === "pptx") {
    const { buildPptx } = await import(
      /* webpackChunkName: "file-pptx" */ "./files/pptx"
    );
    blob = await buildPptx(text, title);
  } else {
    const { buildXlsx } = await import(
      /* webpackChunkName: "file-xlsx" */ "./files/xlsx"
    );
    blob = await buildXlsx(text);
  }
  const url = URL.createObjectURL(blob);
  save(url, name);
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
}

// Downloads a make as its own kind of file
export function downloadMake({ output, text, title, image }) {
  const file = OUTPUT_FILES[output];
  if (!file) return Promise.resolve();
  return downloadFile({
    format: file.format,
    text,
    title,
    image,
    plain: output === "text" || output === "image",
  });
}
